import {
  CustomSelector,
  ECustomSelector,
  SupportingText,
  Toggle,
  Typography,
} from '@globalmed/atlas-ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ConfigurationTab.module.css';
import { TCreateOrganizationForm } from '../../schema';
import { useGlobalMedDisclaimer } from '../../utils/useGlobalMedDisclaimer';

type TProps = {
  disabled?: boolean;
};

/**
 * Configuration tab of the create organization modal.
 * @param props - Component props.
 * @param props.disabled - When true, all toggles are locked for editing
 * (Org Admins) and show a disclaimer tooltip on hover.
 * @returns JSX.Element representing the configuration tab.
 */
export const ConfigurationTab = ({ disabled = false }: TProps) => {
  const {
    control,
    formState: { isSubmitting },
    watch,
    trigger,
    clearErrors,
    setValue,
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const withDisclaimer = useGlobalMedDisclaimer(disabled);

  const interactiveTriageEnabled = watch('configuration.symptomTriage');
  const triageProvider = watch('configuration.triageProvider');

  return (
    <div className={styles.settingsContainer}>
      <div className={styles.settingItem}>
        <Controller
          name="configuration.tfa"
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <SupportingText text={t('admin-panel.two-factor-authentication')}>
                <Toggle
                  disabled={isSubmitting || disabled}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  aria-label={t('admin-panel.two-factor-authentication')}
                />
              </SupportingText>,
            )
          }
        />
        <Typography color={'var(--colors-gray600)'} sx={{ lineHeight: '20px' }}>
          {t('create-organization-modal.two-factor-authentication-description')}
        </Typography>
      </div>

      <div className={styles.settingItem}>
        <Controller
          name="configuration.symptomTriage"
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <SupportingText text={t('admin-panel.interactive-triage')}>
                <Toggle
                  disabled={isSubmitting || disabled}
                  checked={!!field.value}
                  onChange={(e) => {
                    const interactiveEnabledNext = e.target.checked;
                    field.onChange(interactiveEnabledNext);
                    if (!interactiveEnabledNext) {
                      setValue('configuration.triageProvider', undefined);
                      clearErrors('configuration.triageProvider');
                    }
                  }}
                  aria-label={t('admin-panel.interactive-triage')}
                />
              </SupportingText>,
            )
          }
        />
        <Typography color={'var(--colors-gray600)'} sx={{ lineHeight: '20px' }}>
          {t('create-organization-modal.interactive-triage-description')}
        </Typography>
      </div>

      {interactiveTriageEnabled && (
        <div className={styles.settingItem}>
          <Controller
            name="configuration.triageProvider"
            control={control}
            render={({ field }) => (
              <div className={styles.flexColumn}>
                {withDisclaimer(
                  <CustomSelector
                    type={ECustomSelector.Select}
                    labelProps={{
                      label: t('admin-panel.symptom-checker-provider'),
                      required: true,
                    }}
                    placeholder={t('admin-panel.select-provider')}
                    options={[
                      {
                        id: 'infermedica',
                        title: t('admin-panel.infermedica'),
                      },
                      { id: 'isabel', title: t('admin-panel.isabel') },
                    ]}
                    selected={field.value ?? ''}
                    onChange={(value) => {
                      field.onChange(value || undefined);
                      void trigger('configuration.triageProvider');
                    }}
                    disabled={isSubmitting || disabled}
                    sx={{ maxWidth: '500px', width: '100%' }}
                  />,
                )}

                {triageProvider === 'isabel' && (
                  <Typography
                    color={'var(--colors-gray600)'}
                    sx={{ lineHeight: '20px' }}
                  >
                    {t('admin-panel.isabel-triage-description')}
                  </Typography>
                )}
              </div>
            )}
          />
        </div>
      )}

      <div className={styles.settingItem}>
        <Controller
          name="configuration.medicalStationCalls"
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <SupportingText
                text={t('create-organization-modal.medical-station-calls')}
              >
                <Toggle
                  disabled={isSubmitting || disabled}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  aria-label={t(
                    'create-organization-modal.medical-station-calls',
                  )}
                />
              </SupportingText>,
            )
          }
        />
        <Typography color={'var(--colors-gray600)'} sx={{ lineHeight: '20px' }}>
          {t('create-organization-modal.medical-station-calls-description')}
        </Typography>
      </div>

      <div className={styles.settingItem}>
        <Controller
          name="configuration.canarySpeech"
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <SupportingText
                text={t('create-organization-modal.canary-speech')}
              >
                <Toggle
                  disabled={isSubmitting || disabled}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  aria-label={t('create-organization-modal.canary-speech')}
                />
              </SupportingText>,
            )
          }
        />
        <Typography color={'var(--colors-gray600)'} sx={{ lineHeight: '20px' }}>
          {t(
            'admin-panel.enable-canary-speech-to-allow-the-system-to-analyze-speech-patterns-during-session-providing-the-clinician-with-objective-real-time-insights-into-patient-stress-mood-and-cognitive-wellness',
          )}
        </Typography>
      </div>

      <div className={styles.settingItem}>
        <Controller
          name="configuration.medwand"
          control={control}
          render={({ field }) =>
            withDisclaimer(
              <SupportingText text={t('admin-panel.medwand')}>
                <Toggle
                  disabled={isSubmitting || disabled}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  aria-label={t('admin-panel.medwand')}
                />
              </SupportingText>,
            )
          }
        />
        <Typography color={'var(--colors-gray600)'} sx={{ lineHeight: '20px' }}>
          {t('admin-panel.enable-medwand-description')}
        </Typography>
      </div>
    </div>
  );
};
